import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import type { WorkflowDefinitionRow, WorkflowModuleRow } from "@/workflows/types";

type ToggleRow = { workflow_definition_id: string; enabled: boolean };

async function fetchTenantToggles(companyId: string | null): Promise<Map<string, boolean>> {
  if (!companyId) return new Map();
  const { data } = await (supabase as any)
    .from("company_workflow_definitions")
    .select("workflow_definition_id, enabled")
    .eq("company_id", companyId);
  const map = new Map<string, boolean>();
  for (const row of (data ?? []) as ToggleRow[]) map.set(row.workflow_definition_id, row.enabled);
  return map;
}

/** Check whether a workflow definition is enabled for the given tenant.
 *  FIX-305: only an explicit enabled=true row turns a workflow on; no row = off. */
export function isEnabledForTenant(toggles: Map<string, boolean>, definitionId: string): boolean {
  return toggles.get(definitionId) === true;
}

export function useWorkflowDefinitions(opts: { onlyActive?: boolean; onlyEnabledForTenant?: boolean } = {}) {
  const onlyActive = opts.onlyActive ?? true;
  const onlyEnabled = opts.onlyEnabledForTenant ?? true;
  const { id: companyId } = useCurrentCompany();
  return useQuery({
    queryKey: ["workflow_definitions", onlyActive, onlyEnabled, companyId],
    queryFn: async () => {
      let q = supabase.from("workflow_definitions" as any).select("*").order("sort_order", { ascending: true });
      if (onlyActive) q = q.eq("is_active", true);
      const { data, error } = await q;
      if (error) throw error;
      const defs = (data ?? []) as unknown as WorkflowDefinitionRow[];
      if (!onlyEnabled) return defs;
      if (!companyId) return [];
      const toggles = await fetchTenantToggles(companyId);
      return defs.filter((d) => isEnabledForTenant(toggles, d.id));
    },
  });
}

export function useWorkflowDefinition(key: string | undefined) {
  const { id: companyId } = useCurrentCompany();
  return useQuery({
    queryKey: ["workflow_definition", key, companyId],
    enabled: !!key,
    queryFn: async () => {
      const { data: defs, error: e1 } = await supabase
        .from("workflow_definitions" as any)
        .select("*")
        .eq("key", key!)
        .maybeSingle();
      if (e1) throw e1;
      if (!defs) return null;
      const def = defs as unknown as WorkflowDefinitionRow;
      const { data: mods, error: e2 } = await supabase
        .from("workflow_modules" as any)
        .select("*")
        .eq("workflow_definition_id", def.id)
        .order("position", { ascending: true });
      if (e2) throw e2;
      const toggles = await fetchTenantToggles(companyId);
      const disabled = !def.is_active || !isEnabledForTenant(toggles, def.id);
      return {
        definition: def,
        modules: (mods ?? []) as unknown as WorkflowModuleRow[],
        disabled,
      };
    },
  });
}

/** Imperative tenant check usable in event handlers. */
export async function isWorkflowEnabledForCompany(workflowKey: string, companyId: string | null): Promise<boolean> {
  if (!companyId) return false;
  const { data: def } = await (supabase as any)
    .from("workflow_definitions")
    .select("id, is_active")
    .eq("key", workflowKey)
    .maybeSingle();
  if (!def?.id || !def.is_active) return false;
  const { data: row } = await (supabase as any)
    .from("company_workflow_definitions")
    .select("enabled")
    .eq("company_id", companyId)
    .eq("workflow_definition_id", def.id)
    .maybeSingle();
  return row?.enabled === true;
}

/** Keys of active workflows switched on for the current company. */
export function useEnabledWorkflowKeys() {
  const q = useWorkflowDefinitions();
  return { keys: new Set((q.data ?? []).map((d) => d.key)), isLoading: q.isLoading };
}

/** Phase time estimates only matter when repair or refurbishment is on. */
export function usePhaseTimesAvailable() {
  const { keys, isLoading } = useEnabledWorkflowKeys();
  return { available: keys.has("repair") || keys.has("refurbishment"), isLoading };
}
